import { Loader2Icon, XIcon } from 'lucide-react';
import { useEffect, useRef, useState, type JSX } from 'react';

import { notify } from '@/lib/notify.js';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { DialogClose } from '@/components/ui/dialog.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import { Textarea } from '@/components/ui/textarea.js';

import type {
  ConnectionProfilePatch,
  ConnectorConnection,
} from '../../services/integration-api.js';

import {
  ConnectionDetailsFields,
  EMPTY_CONNECTION_DETAILS,
  type ConnectionDetailsValue,
} from './ConnectionDetailsFields.js';

/** What the dialog hands back: the new connection's details, plus any repairs to the existing ones. */
export interface ConnectionSetupResult {
  details: ConnectionDetailsValue;
  /** Keyed by `connectionId`. Empty unless the server asked for the existing connections to be named. */
  peerPatches: Map<string, ConnectionProfilePatch>;
}

/**
 * Asked before the connect leaves for the provider: what to call the new connection and when the agent
 * should reach for it.
 *
 * It exists because there is no second chance on the OAuth path — the callback creates the row, and a
 * second connection of a provider is only useful once both of them say what they are for. The dialog is
 * therefore opened exactly where the answer is required: the caller already holds a connection of this
 * provider here.
 *
 * `conflictMessage` is the service's own 409 text, shown VERBATIM. It means an existing connection is
 * missing a name or an instruction too, so the dialog then also asks for those and hands them back as
 * `peerPatches` — filling in both connections on one screen is the only way out of that refusal, and
 * sending the person off to another card to find the empty field is how a retry loop starts.
 */
export function ConnectionSetupDialog({
  open,
  onOpenChange,
  providerName,
  peers,
  personalWorkspace,
  conflictMessage,
  busy = false,
  explainUnavailableActions = false,
  onDismissWhileBusy,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerName: string;
  /** The caller's existing connections of this provider here — what the new one has to be told apart from. */
  peers: ConnectorConnection[];
  /** Whether the workspace being connected in is the caller's own personal one. */
  personalWorkspace: boolean;
  conflictMessage: string | null;
  busy?: boolean;
  explainUnavailableActions?: boolean;
  /**
   * Called when the person dismisses the dialog — a click outside or Escape — while it is busy. Given,
   * the dismiss cancels that work and closes the dialog; absent, a busy dialog refuses to close.
   */
  onDismissWhileBusy?: () => void;
  onConfirm: (result: ConnectionSetupResult) => void | Promise<void>;
}): JSX.Element {
  const confirming = useRef(false);
  const warnBusy = (): void => {
    notify.warning(
      'A connection request is running. Finish sign-in, or cancel it before trying again.',
    );
  };
  const changeOpen = (next: boolean): void => {
    if (!next && (busy || confirming.current)) {
      if (!onDismissWhileBusy) {
        if (explainUnavailableActions)
          notify.warning(
            'Connection details have already been sent. Wait for saving to finish before closing.',
          );
        return;
      }
      onDismissWhileBusy();
    }
    onOpenChange(next);
  };
  const [details, setDetails] = useState<ConnectionDetailsValue>(
    EMPTY_CONNECTION_DETAILS,
  );
  const [peerDetails, setPeerDetails] = useState<
    Record<string, { label: string; usageHint: string }>
  >({});

  /**
   * The connections that will need a name and an instruction alongside the new one, decided BEFORE the
   * service is asked rather than after it refuses.
   *
   * Presence already says whether a value resolves: an instruction is absent outright, and a name whose
   * `labelFrom` is `default` is the computed one, which §3 does not count — "a name nobody chose is not
   * an answer to which of these two do you mean". So the dialog can see the refusal coming and collect
   * the repair in the same pass.
   *
   * Waiting for the 409 cost the person their work on both lanes: a sign-in opened a popup that could
   * only fail, and an api-key connect got the refusal INSIDE the credentials form, where nothing
   * reopens this dialog — the message named a connection the person had no field to fix. Hence "before",
   * not "after". A conflict message still forces every peer in, because the service has then named one
   * this rule did not predict.
   */
  const peersNeedingRepair = peers.filter(
    (peer) =>
      conflictMessage !== null ||
      (peer.usageHint ?? '').trim().length === 0 ||
      peer.labelFrom === 'default',
  );

  /** What each existing connection is currently called, as the wire reports it — the seed for a repair. */
  const peerSeed = JSON.stringify(
    peers.map((peer) => [peer.connectionId, peer.label, peer.usageHint ?? '']),
  );

  /**
   * Seeds the fields when the dialog opens, and re-seeds only when the connections themselves change.
   * Keying on the seed STRING rather than the array is what keeps a half-typed repair from being wiped
   * by an unrelated presence refetch handing down a fresh array of identical values. A reopen that
   * carries the service's refusal keeps what was typed, so the retry is one press.
   */
  useEffect(() => {
    if (!open || conflictMessage !== null) return;
    setDetails(EMPTY_CONNECTION_DETAILS);
    setPeerDetails(
      Object.fromEntries(
        (JSON.parse(peerSeed) as [string, string, string][]).map(
          ([connectionId, label, usageHint]) => [
            connectionId,
            { label, usageHint },
          ],
        ),
      ),
    );
  }, [open, peerSeed, conflictMessage]);

  /**
   * The note under the instruction field. In a working workspace these values are that workspace's own
   * profile; in the caller's personal space they are the connection's base values, which every other
   * workspace falls back to.
   */
  const scopeNote = personalWorkspace
    ? 'This is the connection’s own description, used in every workspace that has nothing more specific to say.'
    : 'The name and the instruction apply in this workspace only. Your other workspaces keep what they already say about this connection.';

  const detailsComplete =
    details.label.trim().length > 0 && details.usageHint.trim().length > 0;
  const peersComplete =
    peersNeedingRepair.length === 0 ||
    peersNeedingRepair.every((peer) => {
      const entry = peerDetails[peer.connectionId];
      return (
        (entry?.label.trim().length ?? 0) > 0 &&
        (entry?.usageHint.trim().length ?? 0) > 0
      );
    });

  /** Only the repairs the conflict asked for travel back; a plain connect sends none. */
  const collectPeerPatches = (): Map<string, ConnectionProfilePatch> => {
    const patches = new Map<string, ConnectionProfilePatch>();
    for (const peer of peersNeedingRepair) {
      const entry = peerDetails[peer.connectionId];
      if (!entry) continue;
      patches.set(peer.connectionId, {
        label: entry.label.trim(),
        usageHint: entry.usageHint.trim(),
      });
    }
    return patches;
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={changeOpen}
      title={`Connect another ${providerName}`}
      description={`Name the ${providerName} connection you are about to add, so you and the agent can tell it from the one you already have.`}
      titleHidden
      showCloseButton={false}
      className="flex w-full max-h-[85vh] max-w-[var(--dialog-w-sm)] flex-col gap-0 overflow-hidden rounded-xl border-border/50 p-0 shadow-xl"
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pt-6 pb-2">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold tracking-tight text-foreground">
            Connect another {providerName}
          </h3>
          <DialogClose asChild>
            <button
              type="button"
              aria-label="Close"
              disabled={!explainUnavailableActions && busy}
              className="-mt-1 -mr-1 flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
            >
              <XIcon className="size-4" />
            </button>
          </DialogClose>
        </div>

        <p className="mt-2 text-compact leading-relaxed text-muted-foreground">
          You already have a {providerName} here. Give this one a name and say
          when the agent should reach for it, or neither of you will know which
          is which.
        </p>

        {conflictMessage !== null && (
          <p
            role="alert"
            className="mt-3 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-caption leading-relaxed text-foreground"
          >
            {conflictMessage}
          </p>
        )}

        <div className="mt-4">
          <ConnectionDetailsFields
            idPrefix="connection-setup"
            providerName={providerName}
            value={details}
            onChange={(next) => {
              if (busy || confirming.current) {
                if (explainUnavailableActions) warnBusy();
                return;
              }
              setDetails(next);
            }}
            scopeNote={scopeNote}
            required
            disabled={!explainUnavailableActions && busy}
          />
        </div>

        {peersNeedingRepair.length > 0 && (
          <div className="mt-5 flex flex-col gap-4 border-t border-border/60 pt-4">
            <p className="text-caption font-medium text-foreground">
              Your existing {providerName} connections need the same
            </p>
            {peersNeedingRepair.map((peer) => (
              <div
                key={peer.connectionId}
                role="group"
                aria-label={`${peer.label} — the connection you already have`}
                className="flex flex-col gap-1.5"
              >
                <Label htmlFor={`peer-label-${peer.connectionId}`}>Name</Label>
                <Input
                  id={`peer-label-${peer.connectionId}`}
                  value={peerDetails[peer.connectionId]?.label ?? ''}
                  disabled={busy}
                  onChange={(event) =>
                    setPeerDetails((current) => ({
                      ...current,
                      [peer.connectionId]: {
                        label: event.target.value,
                        usageHint: current[peer.connectionId]?.usageHint ?? '',
                      },
                    }))
                  }
                />
                <Label htmlFor={`peer-usage-hint-${peer.connectionId}`}>
                  When to use it
                </Label>
                <Textarea
                  id={`peer-usage-hint-${peer.connectionId}`}
                  rows={2}
                  maxLength={500}
                  value={peerDetails[peer.connectionId]?.usageHint ?? ''}
                  disabled={busy}
                  onChange={(event) =>
                    setPeerDetails((current) => ({
                      ...current,
                      [peer.connectionId]: {
                        label: current[peer.connectionId]?.label ?? '',
                        usageHint: event.target.value,
                      },
                    }))
                  }
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 px-6 pb-5 pt-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => changeOpen(false)}
          disabled={!explainUnavailableActions && busy}
        >
          Cancel
        </Button>
        <Button
          variant="default"
          size="sm"
          disabled={
            !explainUnavailableActions &&
            (busy || !detailsComplete || !peersComplete)
          }
          onClick={() => {
            if (busy || confirming.current) {
              if (explainUnavailableActions) warnBusy();
              return;
            }
            if (!detailsComplete || !peersComplete) {
              if (explainUnavailableActions)
                notify.error(
                  'Enter a name and when to use it for the new connection and each existing connection shown here.',
                );
              return;
            }
            confirming.current = true;
            void (async () => {
              try {
                await onConfirm({ details, peerPatches: collectPeerPatches() });
              } finally {
                confirming.current = false;
              }
            })();
          }}
        >
          {busy && (
            <Loader2Icon className="size-3.5 animate-spin" aria-hidden="true" />
          )}
          Continue
        </Button>
      </div>
    </AppDialog>
  );
}
